import { useEffect, useState } from 'react';
import { Copy, Eye, EyeOff, RefreshCw } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import type { AdminMemberDetail } from '../hooks/useAdminUsersQuery';
import { useMemberPasswordSetMutation } from '../hooks/useMemberCsMutations';
import { generateMemberPassword, memberPasswordProblems, MEMBER_PASSWORD_MAX } from '../lib/memberPassword';

const REASON_MAX = 200;

interface MemberPasswordSetDialogProps {
  user: Pick<AdminMemberDetail, 'userId' | 'name'>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * QA-394 — 관리자 비밀번호 직접 지정(고도몰 회원정보 [비밀번호] 입력과 같은 흐름). 인증이 안 되는 고객이 CS 로
 * 들어오면 관리자가 새 비밀번호를 정하고, 고객에게 직접 알려 준다(BE 는 비밀번호 없이 "변경 완료" 메일만 보냄).
 * - [랜덤 생성]: 규칙을 만족하는 비밀번호를 만들어 두 칸에 넣고 바로 보이게 한다. [복사]로 클립보드에.
 * - 비밀번호는 이 창의 메모리 상태에만 있다(저장소·쿼리키·로그·에러 리포트에 싣지 않음, data-sentry-mask).
 *   저장 후에는 고객 안내용으로 이 창을 닫을 때까지만 보여 주고, 닫으면 모두 비운다.
 */
export const MemberPasswordSetDialog = ({ user, open, onOpenChange }: MemberPasswordSetDialogProps) => {
  const { t } = useTranslation('common');
  const mutation = useMemberPasswordSetMutation();
  const { reset: resetMutation } = mutation;
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [show, setShow] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) {
      setPw('');
      setConfirm('');
      setReason('');
      setShow(false);
      setDone(null);
      setCopied(false);
      setTouched(false);
      resetMutation();
    }
  }, [open, resetMutation]);

  const rule = t('users.cs.pwRule', '영문·숫자·특수문자를 모두 포함해 10자 이상으로 입력해 주세요.');
  const problems = memberPasswordProblems(pw);
  const mismatch = confirm !== '' && pw !== confirm;
  const reasonValid = reason.trim() !== '' && reason.trim().length <= REASON_MAX;
  const canSubmit = problems.length === 0 && pw === confirm && reasonValid && !mutation.isPending;

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const generate = () => {
    const next = generateMemberPassword();
    setPw(next);
    setConfirm(next);
    setShow(true);
    setTouched(true);
    setCopied(false);
  };

  const err = mutation.error;
  const errText = !err
    ? null
    : err.code === 'USER_PASSWORD_POLICY_FAILED'
      ? rule
      : err.status === 409
        ? t('users.cs.pwConflict', '탈퇴·정지 회원은 비밀번호를 변경할 수 없습니다.')
        : err.message;

  const inputType = show ? 'text' : 'password';
  const copyButton = (value: string, testId: string) => (
    <Button type="button" variant="outline" size="sm" onClick={() => void copy(value)} disabled={!value} data-testid={testId}>
      <Copy size={14} aria-hidden="true" />
      {copied ? t('users.cs.pwCopied', '복사됨') : t('users.cs.pwCopy', '복사')}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px]" data-testid="password-set-dialog">
        <DialogHeader>
          <DialogTitle>{t('users.cs.pwTitle', '비밀번호 초기화')}</DialogTitle>
          <DialogDescription>
            {t(
              'users.cs.pwDesc',
              '새 비밀번호를 정해 저장합니다. 저장하면 고객의 기존 로그인이 끊기고, 고객에게 "비밀번호 변경 완료" 메일이 발송됩니다(비밀번호는 메일에 없음). 새 비밀번호는 고객에게 직접 안내해 주세요.',
            )}
          </DialogDescription>
        </DialogHeader>

        {done !== null ? (
          <div className="flex flex-col gap-2 py-1" data-sentry-mask>
            <p
              role="status"
              className="rounded px-2 py-2 text-sm"
              style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
              data-testid="password-set-done"
            >
              {t('users.cs.pwDone', '비밀번호가 변경되었습니다. 고객에게 새 비밀번호를 안내해 주세요. (고객에게 변경 완료 메일이 발송됩니다)')}
            </p>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 rounded px-2 py-1.5 font-mono text-sm"
                style={{ background: 'var(--surface-1)', color: 'var(--content-strong)' }}
                data-testid="password-set-done-value"
              >
                {done}
              </code>
              {copyButton(done, 'password-set-done-copy')}
            </div>
            <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {t('users.cs.pwDoneHint', '이 창을 닫으면 비밀번호는 다시 볼 수 없습니다.')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3 py-1" data-sentry-mask>
            <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
              <span>
                {t('users.cs.pwNew', '새 비밀번호')}
                <span style={{ color: 'var(--signal-danger)' }}> *</span>
              </span>
              <div className="flex items-center gap-1.5">
                <Input
                  type={inputType}
                  value={pw}
                  maxLength={MEMBER_PASSWORD_MAX}
                  autoComplete="new-password"
                  spellCheck={false}
                  onChange={(e) => {
                    setPw(e.target.value);
                    setTouched(true);
                    setCopied(false);
                  }}
                  aria-label={t('users.cs.pwNew', '새 비밀번호')}
                  className="flex-1 font-mono"
                  data-testid="password-set-new"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShow((v) => !v)}
                  aria-label={show ? t('users.cs.pwHide', '비밀번호 숨기기') : t('users.cs.pwShow', '비밀번호 보기')}
                  data-testid="password-set-toggle"
                >
                  {show ? <EyeOff size={14} aria-hidden="true" /> : <Eye size={14} aria-hidden="true" />}
                </Button>
                {copyButton(pw, 'password-set-copy')}
              </div>
            </label>
            <div className="-mt-1 flex items-center justify-between gap-2">
              <p
                className="text-[11px]"
                style={{ color: touched && problems.length > 0 ? 'var(--status-danger-fg)' : 'var(--content-muted)' }}
                data-testid="password-set-rule"
              >
                {rule}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={generate} data-testid="password-set-generate">
                <RefreshCw size={14} aria-hidden="true" />
                {t('users.cs.pwGenerate', '랜덤 생성')}
              </Button>
            </div>
            <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
              <span>
                {t('users.cs.pwConfirm', '비밀번호 확인')}
                <span style={{ color: 'var(--signal-danger)' }}> *</span>
              </span>
              <Input
                type={inputType}
                value={confirm}
                maxLength={MEMBER_PASSWORD_MAX}
                autoComplete="new-password"
                spellCheck={false}
                onChange={(e) => setConfirm(e.target.value)}
                aria-label={t('users.cs.pwConfirm', '비밀번호 확인')}
                className="font-mono"
                data-testid="password-set-confirm"
              />
            </label>
            {mismatch ? (
              <p className="-mt-1 text-[11px]" style={{ color: 'var(--status-danger-fg)' }} data-testid="password-set-mismatch">
                {t('users.cs.pwMismatch', '두 비밀번호가 다릅니다.')}
              </p>
            ) : null}
            <label className="grid gap-1 text-xs" style={{ color: 'var(--content-muted)' }}>
              <span>
                {t('users.cs.resetReason', '사유')}
                <span style={{ color: 'var(--signal-danger)' }}> *</span>
              </span>
              <Input
                value={reason}
                maxLength={REASON_MAX}
                placeholder={t('users.cs.pwReasonPlaceholder', '예: 고객 전화 요청 — 본인인증 불가로 비밀번호 초기화')}
                onChange={(e) => setReason(e.target.value)}
                aria-label={t('users.cs.resetReason', '사유')}
                data-testid="password-set-reason"
              />
            </label>
            {errText ? (
              <p
                role="alert"
                className="rounded px-2 py-1.5 text-[12px]"
                style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
                data-testid="password-set-error"
              >
                {errText}
              </p>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {done !== null ? (
            <Button type="button" size="sm" onClick={() => onOpenChange(false)}>
              {t('users.cs.close', '닫기')}
            </Button>
          ) : (
            <>
              <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                {t('users.cs.cancel', '취소')}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!canSubmit}
                onClick={() => {
                  const value = pw;
                  mutation.mutate(
                    { userId: user.userId, newPassword: value, reason: reason.trim() },
                    {
                      onSuccess: () => {
                        // 입력칸은 비우고, 고객 안내용으로 창을 닫을 때까지만 보여 준다.
                        setPw('');
                        setConfirm('');
                        setDone(value);
                      },
                    },
                  );
                }}
                data-testid="password-set-submit"
              >
                {t('users.cs.pwSubmit', '비밀번호 변경')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
